import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export interface JtSyncStatus {
  candidates: number;
  contacts: number;
  jobs: number;
  pipelines: number;
  deals: number;
  applications: number;
  last_sync_at: string | null;
  last_sync_type: string | null;
  /** How background jobs reach JobTetris (JT1-1850). Never includes the key itself. */
  credential: {
    kind: 'USER_TOKEN' | 'API_KEY';
    key_prefix: string | null;
    last_used_at: string | null;
    last_failure_at: string | null;
    last_failure_reason: string | null;
  } | null;
}

/** Result of a sync run. Counts per entity, plus an optional `errors` map
 *  keyed by sync name (e.g. `deals`) explaining why that count is 0. */
export interface JtSyncResult {
  [key: string]: number | Record<string, string> | undefined;
  errors?: Record<string, string>;
}

export function useJtSyncStatus(options: { enabled?: boolean } = {}) {
  return useQuery<JtSyncStatus>({
    queryKey: ['jt-sync', 'status'],
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: JtSyncStatus }>('/jt-sync/status');
      return data.data;
    },
  });
}

export function useRunJtSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      jt_token: string;
      scope?: 'all' | 'candidates' | 'contacts' | 'jobs' | 'pipelines' | 'deals' | 'applications';
    }) => {
      const { data } = await apiClient.post<{ data: JtSyncResult }>('/jt-sync/run', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jt-sync'] });
      qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });
}
